import React, { useEffect, useState } from 'react';
import { useContracts } from '../../hooks/useContracts';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errorParser';
import AdminSection from './AdminSection';
import ButtonLoader from '../common/ButtonLoader';

const UNITS = [
  { id: 'sec',  label: 'Seconds', mul: 1     },
  { id: 'min',  label: 'Minutes', mul: 60    },
  { id: 'hour', label: 'Hours',   mul: 3600  },
  { id: 'day',  label: 'Days',    mul: 86400 }
];

// Hard cap from the contract: 30 days = 2,592,000 seconds
const MAX_SECONDS = 30 * 86400;

const AdminSystem = ({ role, refreshKey, onChanged }) => {
  const { coreStorage } = useContracts();
  const notify = useNotify();
  const [holdCurrent, setHoldCurrent] = useState('0');
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState('day');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!coreStorage?.read) return;
      try {
        const h = await coreStorage.read.holdDuration().catch(() => 0);
        if (!cancelled) setHoldCurrent(h.toString());
      } catch {}
    };
    load();
    return () => { cancelled = true; };
  }, [coreStorage, refreshKey]);

  const computeSeconds = () => {
    const n = Number(value);
    if (!n || n <= 0) return 0;
    const mul = UNITS.find((u) => u.id === unit)?.mul || 1;
    return Math.floor(n * mul);
  };

  const updateHold = async () => {
    if (!coreStorage?.write) return notify.error('Wallet', 'Connect wallet first');
    const seconds = computeSeconds();
    if (seconds <= 0) return notify.error('Invalid', 'Enter a value > 0');
    if (seconds > MAX_SECONDS) return notify.error('Out of range', 'Max is 30 days');

    setBusy(true);
    try {
      const tx = await coreStorage.write.updateHoldDuration(seconds);
      notify.info('Submitting', 'Hold duration update sent…');
      await tx.wait();
      notify.success('Updated', `Hold duration set to ${seconds}s`);
      setValue('');
      onChanged && onChanged();
    } catch (e) {
      notify.error('Failed', parseError(e));
    } finally {
      setBusy(false);
    }
  };

  const currentSec = Number(holdCurrent) || 0;
  const previewSec = computeSeconds();

  return (
    <AdminSection
      title="Hold Duration"
      subtitle="Window during which sponsor income held for a non-eligible upline can still be claimed (after they upgrade)."
      role={role}
    >
      <div className="admin-tile">
        <div className="admin-tile-meta">
          Current:&nbsp;
          <strong>{currentSec.toLocaleString()}s</strong>&nbsp;·&nbsp;
          {(currentSec / 3600).toFixed(2)} hours&nbsp;·&nbsp;
          {(currentSec / 86400).toFixed(2)} days
        </div>

        <div className="admin-form-row" style={{ marginTop: 12 }}>
          <label>New Hold Duration</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              placeholder="e.g. 5"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              style={{ flex: 1 }}
            />
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              style={{ width: 130 }}
            >
              {UNITS.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
            </select>
          </div>
        </div>

        {previewSec > 0 && (
          <div className="admin-tile-meta" style={{ marginTop: 8 }}>
            = <strong style={{ color: 'var(--bnb-yellow)' }}>{previewSec.toLocaleString()} seconds</strong>
            {previewSec > MAX_SECONDS && (
              <span style={{ color: 'var(--error)', marginLeft: 10 }}>Exceeds 30-day cap</span>
            )}
          </div>
        )}

        <button
          className="btn btn-primary btn-block"
          style={{ marginTop: 12 }}
          onClick={updateHold}
          disabled={busy || previewSec <= 0 || previewSec > MAX_SECONDS}
        >
          {busy ? <ButtonLoader label="Updating" /> : 'Update Hold Duration'}
        </button>

        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10 }}>
          Range: 1 second – 30 days. Existing held entries are unaffected; only new holds use the new window.
        </div>
      </div>
    </AdminSection>
  );
};

export default AdminSystem;
